import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, MapPin, Ticket, DollarSign, BarChart3, Eye, Plus, Pencil, Trash2, Building2, Layers, Users, ClipboardList, ShieldAlert, Ban, HelpCircle, Download, ArrowUp, ArrowDown, Minus, LayoutDashboard, List } from "lucide-react";
import { getDashboardData } from "@/lib/dashboard.functions";
import { formatCurrency } from "@/lib/money";
import { deleteEvent, cancelEvent } from "@/lib/events.functions";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, AreaChart, Area } from "recharts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AccessGate } from "@/components/permissions/AccessGate";


interface DashboardEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  city: string;
  category: string;
  is_published: boolean;
  image_url: string | null;
  organizer_id: string;
  status?: string | null;
  deleted_at?: string | null;
  is_owner?: boolean;
  ticket_types: {
    id: string;
    name: string;
    price: number;
    total: number;
    available: number;
  }[];
  tickets: {
    id: string;
    status: string;
    price: number;
    purchased_at: string;
  }[];
}

export const Route = createFileRoute("/dashboard/")({
  head: () => ({
    meta: [
      { title: "Dashboard — ZYNTRA" },
      { name: "description", content: "Panel de organizador. Gestioná tus eventos, ventas y tickets." },
    ],
  }),
  component: DashboardGuarded,
});

function DashboardGuarded() {
  return (
    <AccessGate required="any-tenant">
      <DashboardPage />
    </AccessGate>
  );
}

function groupByDay(tickets: { status: string; price: number; purchased_at: string }[]) {
  const map = new Map<string, { tickets: number; revenue: number }>();
  for (const t of tickets) {
    if (t.status === "cancelled") continue;
    const day = t.purchased_at.slice(0, 10);
    const entry = map.get(day) || { tickets: 0, revenue: 0 };
    entry.tickets += 1;
    entry.revenue += Number(t.price);
    map.set(day, entry);
  }
  const sorted = Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  return sorted.map(([date, data]) => ({
    date,
    label: new Date(date + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" }),
    ...data,
  }));
}

function groupByWeek(tickets: { status: string; price: number; purchased_at: string }[]) {
  const map = new Map<string, { tickets: number; revenue: number; start: string }>();
  for (const t of tickets) {
    if (t.status === "cancelled") continue;
    const d = new Date(t.purchased_at);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d);
    monday.setDate(diff);
    const key = monday.toISOString().slice(0, 10);
    const entry = map.get(key) || { tickets: 0, revenue: 0, start: key };
    entry.tickets += 1;
    entry.revenue += Number(t.price);
    map.set(key, entry);
  }
  const sorted = Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  return sorted.map(([, data]) => ({
    date: data.start,
    label: `Sem ${new Date(data.start + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" })}`,
    ...data,
  }));
}

const chartConfig = {
  tickets: { label: "Tickets", color: "var(--primary)" },
  revenue: { label: "Ingresos", color: "var(--accent)" },
};

function monthRange(d: Date) {
  const start = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime();
  return { start, end };
}

function sumTickets(tickets: { status: string; price: number; purchased_at: string }[], from?: number, to?: number) {
  let count = 0, revenue = 0;
  for (const t of tickets) {
    if (t.status === "cancelled") continue;
    if (from !== undefined && to !== undefined) {
      const ts = new Date(t.purchased_at).getTime();
      if (ts < from || ts >= to) continue;
    }
    count += 1;
    revenue += Number(t.price);
  }
  return { count, revenue };
}

function pctDelta(curr: number, prev: number): number | null {
  if (prev === 0) return curr === 0 ? 0 : null;
  return ((curr - prev) / prev) * 100;
}

function downloadCsv(filename: string, rows: (string | number)[][]) {
  const escape = (v: string | number) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = rows.map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function DashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const [events, setEvents] = useState<DashboardEvent[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState<string>("all");
  const [view, setView] = useState<"full" | "summary">("full");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [user, authLoading]);

  async function fetchData() {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const result = await getDashboardData({
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
      } as any);
      setEvents(result.events as DashboardEvent[]);
      setIsAdmin(!!result.isAdmin);
    } catch (err) {
      console.error("Dashboard error:", err);
      setEvents([]);
    }
    setLoading(false);
  }

  const filteredEvents = useMemo(
    () => (selectedEventId === "all" ? events : events.filter((e) => e.id === selectedEventId)),
    [events, selectedEventId],
  );

  const allTickets = useMemo(
    () => filteredEvents.flatMap((e) => e.tickets),
    [filteredEvents],
  );
  const dailyData = useMemo(() => groupByDay(allTickets), [allTickets]);
  const weeklyData = useMemo(() => groupByWeek(allTickets), [allTickets]);

  const totalTicketsSold = filteredEvents.reduce((sum, e) => sum + e.tickets.filter((t) => t.status !== "cancelled").length, 0);
  const totalRevenue = filteredEvents.reduce((sum, e) => sum + e.tickets.filter((t) => t.status !== "cancelled").reduce((s, t) => s + Number(t.price), 0), 0);
  const totalAvailable = filteredEvents.reduce((sum, e) => sum + e.ticket_types.reduce((s, tt) => s + tt.available, 0), 0);
  const totalCapacity = filteredEvents.reduce((sum, e) => sum + e.ticket_types.reduce((s, tt) => s + tt.total, 0), 0);

  // Month-over-month comparison
  const now = new Date();
  const thisMonth = monthRange(now);
  const prevMonth = monthRange(new Date(now.getFullYear(), now.getMonth() - 1, 1));
  const currMonthStats = useMemo(() => sumTickets(allTickets, thisMonth.start, thisMonth.end), [allTickets, thisMonth.start, thisMonth.end]);
  const prevMonthStats = useMemo(() => sumTickets(allTickets, prevMonth.start, prevMonth.end), [allTickets, prevMonth.start, prevMonth.end]);
  const ticketsDelta = pctDelta(currMonthStats.count, prevMonthStats.count);
  const revenueDelta = pctDelta(currMonthStats.revenue, prevMonthStats.revenue);

  function exportCsv() {
    const rows: (string | number)[][] = [
      ["Evento", "Fecha", "Ciudad", "Categoría", "Estado", "Capacidad", "Vendidos", "Disponibles", "% Vendido", "Ingresos (CRC)", "Vendidos mes actual", "Ingresos mes actual (CRC)", "Vendidos mes anterior", "Ingresos mes anterior (CRC)"],
    ];
    for (const e of filteredEvents) {
      const sold = e.tickets.filter((t) => t.status !== "cancelled").length;
      const revenue = e.tickets.filter((t) => t.status !== "cancelled").reduce((s, t) => s + Number(t.price), 0);
      const capacity = e.ticket_types.reduce((s, tt) => s + tt.total, 0);
      const available = e.ticket_types.reduce((s, tt) => s + tt.available, 0);
      const curr = sumTickets(e.tickets, thisMonth.start, thisMonth.end);
      const prev = sumTickets(e.tickets, prevMonth.start, prevMonth.end);
      const pctSold = capacity > 0 ? Math.round((sold / capacity) * 100) : 0;
      const estado = e.deleted_at ? "Eliminado" : e.status === "cancelled" ? "Cancelado" : e.is_published ? "Publicado" : "Borrador";
      rows.push([e.title, e.date, e.city, e.category, estado, capacity, sold, available, `${pctSold}%`, revenue, curr.count, curr.revenue, prev.count, prev.revenue]);
    }
    const stamp = now.toISOString().slice(0, 10);
    const suffix = selectedEventId === "all" ? "todos" : (filteredEvents[0]?.title || "evento").replace(/[^a-z0-9-_]+/gi, "_").slice(0, 40);
    downloadCsv(`zyntra_dashboard_${suffix}_${stamp}.csv`, rows);
  }

  if (authLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <BarChart3 className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-xl font-bold text-foreground">Acceso restringido</h1>
        <p className="text-sm text-muted-foreground">Iniciá sesión para acceder al dashboard de organizador.</p>
        <Link to="/login" className="rounded-lg gradient-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
          Ingresar
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 pt-24 pb-8 sm:px-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">Gestión de eventos y ventas</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex rounded-xl border border-border bg-secondary p-1">
            <button
              onClick={() => setView("summary")}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === "summary" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              title="Vista resumida"
            >
              <LayoutDashboard className="h-3.5 w-3.5" />
              Resumen
            </button>
            <button
              onClick={() => setView("full")}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === "full" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              title="Vista completa"
            >
              <List className="h-3.5 w-3.5" />
              Completo
            </button>
          </div>
          <Link to="/dashboard/venues" className="inline-flex items-center gap-2 rounded-xl border border-border bg-secondary px-4 py-2.5 text-sm font-semibold text-secondary-foreground hover:bg-accent transition-colors">
            <Building2 className="h-4 w-4" />
            Venues
          </Link>
          <Link to="/create-event" className="inline-flex items-center gap-2 rounded-xl gradient-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90 glow-primary">
            <Plus className="h-4 w-4" />
            Crear evento
          </Link>
        </div>
      </div>

      {/* Filter + Export */}
      {events.length > 0 && (
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-muted-foreground">Filtrar:</label>
            <select
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
              className="rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none min-w-[200px]"
            >
              <option value="all">Todos los eventos ({events.length})</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>{e.title}</option>
              ))}
            </select>
          </div>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground hover:bg-accent transition-colors"
          >
            <Download className="h-4 w-4" />
            Exportar CSV
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        <StatCard icon={<CalendarDays className="h-4 w-4" />} label="Eventos" value={filteredEvents.length} />
        <StatCard icon={<Ticket className="h-4 w-4" />} label="Tickets vendidos" value={totalTicketsSold} />
        <StatCard icon={<Eye className="h-4 w-4" />} label="Disponibles" value={totalAvailable} subtitle={`de ${totalCapacity}`} />
        <StatCard icon={<DollarSign className="h-4 w-4" />} label="Ingresos" value={formatCurrency(totalRevenue, "CRC")} />
      </div>

      {/* Month-over-month comparison */}
      <div className="mb-8 grid gap-3 sm:grid-cols-2 sm:gap-4">
        <MoMCard
          label="Tickets vendidos este mes"
          current={currMonthStats.count.toLocaleString("es-CR")}
          previous={`${prevMonthStats.count.toLocaleString("es-CR")} mes anterior`}
          delta={ticketsDelta}
        />
        <MoMCard
          label="Ingresos este mes"
          current={formatCurrency(currMonthStats.revenue, "CRC")}
          previous={`${formatCurrency(prevMonthStats.revenue, "CRC")} mes anterior`}
          delta={revenueDelta}
        />
      </div>

      {view === "summary" ? (
        loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : filteredEvents.length === 0 ? (
          <Card className="glass-card border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <CalendarDays className="mb-4 h-12 w-12 text-muted-foreground" />
              <h2 className="text-lg font-semibold text-foreground">Sin eventos</h2>
              <p className="mt-1 text-sm text-muted-foreground">No hay eventos para mostrar.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-foreground">Tus eventos</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {filteredEvents.map((evt) => {
                  const sold = evt.tickets.filter((t) => t.status !== "cancelled").length;
                  const revenue = evt.tickets.filter((t) => t.status !== "cancelled").reduce((s, t) => s + Number(t.price), 0);
                  const capacity = evt.ticket_types.reduce((s, tt) => s + tt.total, 0);
                  const pct = capacity > 0 ? Math.round((sold / capacity) * 100) : 0;
                  return (
                    <Link
                      key={evt.id}
                      to="/events/$eventId"
                      params={{ eventId: evt.id }}
                      className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-accent/50 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-foreground">{evt.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(evt.date + "T00:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" })} · {evt.city}
                        </p>
                      </div>
                      <div className="hidden sm:flex items-center gap-6 text-xs text-muted-foreground">
                        <div className="text-right">
                          <p className="font-semibold text-foreground">{sold}</p>
                          <p className="text-[10px]">vendidos</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-primary">{formatCurrency(revenue, "CRC")}</p>
                          <p className="text-[10px]">ingresos</p>
                        </div>
                        <div className="text-right w-12">
                          <p className="font-semibold text-foreground">{pct}%</p>
                          <p className="text-[10px]">vendido</p>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )
      ) : (
        <>
          {/* Charts */}
          {!loading && dailyData.length > 0 && (
            <div className="mb-8 grid gap-4 sm:grid-cols-2">
              <Card className="glass-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-foreground">Ventas por día</CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="tickets" className="w-full">
                    <TabsList className="mb-3 h-7">
                      <TabsTrigger value="tickets" className="text-xs px-2 py-0.5">Tickets</TabsTrigger>
                      <TabsTrigger value="revenue" className="text-xs px-2 py-0.5">Ingresos</TabsTrigger>
                    </TabsList>
                    <TabsContent value="tickets">
                      <ChartContainer config={chartConfig} className="aspect-[2/1] w-full">
                        <BarChart data={dailyData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={30} />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Bar dataKey="tickets" fill="var(--color-tickets)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ChartContainer>
                    </TabsContent>
                    <TabsContent value="revenue">
                      <ChartContainer config={chartConfig} className="aspect-[2/1] w-full">
                        <AreaChart data={dailyData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} width={50} tickFormatter={(v) => `₡${Number(v).toLocaleString("es-CR")}`} />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Area type="monotone" dataKey="revenue" fill="var(--color-revenue)" fillOpacity={0.2} stroke="var(--color-revenue)" strokeWidth={2} />
                        </AreaChart>
                      </ChartContainer>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-foreground">Ventas por semana</CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="tickets" className="w-full">
                    <TabsList className="mb-3 h-7">
                      <TabsTrigger value="tickets" className="text-xs px-2 py-0.5">Tickets</TabsTrigger>
                      <TabsTrigger value="revenue" className="text-xs px-2 py-0.5">Ingresos</TabsTrigger>
                    </TabsList>
                    <TabsContent value="tickets">
                      <ChartContainer config={chartConfig} className="aspect-[2/1] w-full">
                        <BarChart data={weeklyData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={30} />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Bar dataKey="tickets" fill="var(--color-tickets)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ChartContainer>
                    </TabsContent>
                    <TabsContent value="revenue">
                      <ChartContainer config={chartConfig} className="aspect-[2/1] w-full">
                        <AreaChart data={weeklyData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} width={50} tickFormatter={(v) => `₡${Number(v).toLocaleString("es-CR")}`} />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Area type="monotone" dataKey="revenue" fill="var(--color-revenue)" fillOpacity={0.2} stroke="var(--color-revenue)" strokeWidth={2} />
                        </AreaChart>
                      </ChartContainer>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : filteredEvents.length === 0 ? (
            <Card className="glass-card border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <CalendarDays className="mb-4 h-12 w-12 text-muted-foreground" />
                <h2 className="text-lg font-semibold text-foreground">No tenés eventos</h2>
                <p className="mt-1 text-sm text-muted-foreground">Creá tu primer evento para empezar a vender entradas.</p>
                <Link to="/dashboard/help/venues" className="mt-4 inline-flex items-center gap-2 text-xs text-primary hover:underline">
                  <HelpCircle className="h-3.5 w-3.5" /> Guía: ligar venue a evento
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredEvents.map((evt) => (
                <EventRow key={evt.id} event={evt} isAdmin={isAdmin} onChanged={fetchData} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MoMCard({ label, current, previous, delta }: { label: string; current: string; previous: string; delta: number | null }) {
  const isUp = delta !== null && delta > 0;
  const isDown = delta !== null && delta < 0;
  const isFlat = delta !== null && delta === 0;
  const color = isUp ? "text-success" : isDown ? "text-destructive" : "text-muted-foreground";
  const bg = isUp ? "bg-success/10" : isDown ? "bg-destructive/10" : "bg-muted";
  const Icon = isUp ? ArrowUp : isDown ? ArrowDown : Minus;
  const deltaLabel = delta === null ? "N/D" : `${isUp ? "+" : ""}${delta.toFixed(1)}%`;
  return (
    <Card className="glass-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground">{label}</p>
            <p className="mt-2 text-xl font-bold text-foreground sm:text-2xl">{current}</p>
            <p className="mt-1 text-xs text-muted-foreground">vs. {previous}</p>
          </div>
          <div className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${bg} ${color}`} title={isFlat ? "Sin cambios" : delta === null ? "Sin datos del mes anterior" : ""}>
            <Icon className="h-3 w-3" />
            {deltaLabel}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}



function StatCard({ icon, label, value, subtitle }: { icon: React.ReactNode; label: string; value: string | number; subtitle?: string }) {
  return (
    <Card className="glass-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          {icon}
          <span className="text-xs font-medium">{label}</span>
        </div>
        <p className="mt-2 text-xl font-bold text-foreground sm:text-2xl">{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}

function EventRow({ event, isAdmin, onChanged }: { event: DashboardEvent; isAdmin: boolean; onChanged: () => void }) {
  const [deleting, setDeleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [actionError, setActionError] = useState("");
  const sold = event.tickets.filter((t) => t.status !== "cancelled").length;
  const revenue = event.tickets.filter((t) => t.status !== "cancelled").reduce((s, t) => s + Number(t.price), 0);
  const available = event.ticket_types.reduce((s, tt) => s + tt.available, 0);
  const total = event.ticket_types.reduce((s, tt) => s + tt.total, 0);
  const pct = total > 0 ? Math.round((sold / total) * 100) : 0;
  const isCancelled = event.status === "cancelled";
  const isDeleted = !!event.deleted_at;
  const isAdminViewingOther = isAdmin && !event.is_owner;

  async function handleDelete() {
    setDeleting(true);
    setActionError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      await deleteEvent({
        data: { eventId: event.id },
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
      } as any);
      onChanged();
    } catch (err: any) {
      setActionError(err.message || "Error al eliminar");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  async function handleCancel() {
    setCancelling(true);
    setActionError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      await cancelEvent({
        data: { eventId: event.id },
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
      } as any);
      setConfirmCancel(false);
      onChanged();
    } catch (err: any) {
      setActionError(err.message || "Error al cancelar");
      setConfirmCancel(false);
    } finally {
      setCancelling(false);
    }
  }

  return (
    <Card className="glass-card transition-all hover:border-primary/30">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link to="/events/$eventId" params={{ eventId: event.id }} className="text-base font-semibold text-foreground hover:text-primary transition-colors truncate">
                {event.title}
              </Link>
              <Badge variant={event.is_published ? "default" : "secondary"} className="text-[10px]">
                {event.is_published ? "Publicado" : "Borrador"}
              </Badge>
              <Link
                to="/edit-event/$eventId"
                params={{ eventId: event.id }}
                className="rounded-md p-1 text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                title="Editar evento"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/dashboard/events/$eventId/venue"
                params={{ eventId: event.id }}
                className="rounded-md p-1 text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                title="Configurar venue/zonas"
              >
                <Layers className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/dashboard/events/$eventId/staff"
                params={{ eventId: event.id }}
                className="rounded-md p-1 text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                title="Gestionar staff del evento"
              >
                <Users className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/dashboard/events/$eventId/validations"
                params={{ eventId: event.id }}
                className="rounded-md p-1 text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                title="Historial de validaciones"
              >
                <ClipboardList className="h-3.5 w-3.5" />
              </Link>
              {!isCancelled && !isDeleted && (
                <button
                  onClick={() => setConfirmCancel(true)}
                  className="rounded-md p-1 text-muted-foreground hover:text-warning hover:bg-warning/10 transition-colors"
                  title="Cancelar evento"
                >
                  <Ban className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                onClick={() => setConfirmDelete(true)}
                className="rounded-md p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                title="Eliminar evento"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              {isAdminViewingOther && (
                <Badge variant="outline" className="border-primary/40 text-primary text-[10px] gap-1">
                  <ShieldAlert className="h-3 w-3" /> Admin
                </Badge>
              )}
              {isCancelled && (
                <Badge variant="destructive" className="text-[10px]">Cancelado</Badge>
              )}
              {isDeleted && (
                <Badge variant="outline" className="border-destructive/40 text-destructive text-[10px]">Eliminado</Badge>
              )}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                {new Date(event.date + "T00:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" })}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {event.city}
              </span>
              <Badge variant="outline" className="text-[10px]">{event.category}</Badge>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 text-center sm:gap-6">
            <div>
              <p className="text-lg font-bold text-foreground">{sold}</p>
              <p className="text-[10px] text-muted-foreground">Vendidos</p>
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{available}</p>
              <p className="text-[10px] text-muted-foreground">Disponibles</p>
            </div>
            <div>
              <p className="text-lg font-bold text-primary">{formatCurrency(revenue, "CRC")}</p>
              <p className="text-[10px] text-muted-foreground">Ingresos</p>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
            <span>{pct}% vendido</span>
            <span>{sold}/{total}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full gradient-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {actionError && (
          <div className="mt-3 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{actionError}</div>
        )}

        {confirmCancel && (
          <div className="mt-3 rounded-xl border border-warning/30 bg-warning/5 p-4">
            <p className="text-sm text-foreground">¿Cancelar <strong>{event.title}</strong>?</p>
            <p className="text-xs text-muted-foreground mt-1">El evento quedará marcado como cancelado y se despublicará. Los tickets vendidos no se borran.</p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="rounded-lg bg-warning px-4 py-2 text-xs font-bold text-warning-foreground disabled:opacity-50"
              >
                {cancelling ? "Cancelando..." : "Sí, cancelar"}
              </button>
              <button
                onClick={() => { setConfirmCancel(false); setActionError(""); }}
                className="rounded-lg border border-border bg-secondary px-4 py-2 text-xs font-medium text-secondary-foreground"
              >
                Volver
              </button>
            </div>
          </div>
        )}

        {confirmDelete && (
          <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
            <p className="text-sm text-foreground">¿Eliminar <strong>{event.title}</strong>?</p>
            <p className="text-xs text-muted-foreground mt-1">
              {isAdmin
                ? "Esta acción es irreversible. Si hay tickets vendidos, el evento será marcado como eliminado en lugar de borrado permanentemente."
                : "Esta acción no se puede deshacer. Solo se pueden eliminar eventos sin tickets vendidos."}
            </p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-lg bg-destructive px-4 py-2 text-xs font-bold text-destructive-foreground disabled:opacity-50"
              >
                {deleting ? "Eliminando..." : "Confirmar"}
              </button>
              <button
                onClick={() => { setConfirmDelete(false); setActionError(""); }}
                className="rounded-lg border border-border bg-secondary px-4 py-2 text-xs font-medium text-secondary-foreground"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
